// 決策按鈕 —— 推進 / 否決 / 暫緩**一張單**的那幾顆。 DESIGN_GUIDE §5.0。
//
// 2026-08-27 使用者：「可以寬度一樣 然後下面按鈕的邏輯依樣嗎?」——
// 上一批只統一了顏色與 padding，量出來仍有兩個差異：
//   (1) 實心按鈕沒有 border → 比同列的外框按鈕**矮 2px**（32 vs 34），並排看得出來；
//   (2) 寬度隨字數跑（117 / 106 / 88…），同一列高低不平。
// 兩個都寫進這裡：`border` 一律有（實心用 transparent），並給一個 min-width。
//
// ⚠ 這裡**只放決策按鈕**。 一般的「儲存 / 新增」仍是 Primary `bg-slate-900`
//   （§5 骨幹尺度），不要拿這支去套 —— 那會把所有按鈕都染成紅綠。
const BASE =
  'inline-flex items-center justify-center gap-1 min-w-[7.5rem] px-3 py-1.5 text-sm rounded border whitespace-nowrap disabled:opacity-50';

const TONE = {
  /** 正向 — 推進流程（核准 / 已覆核 / 標記已付款）。 **只有正向是實心**。 */
  go: 'bg-emerald-600 text-white border-transparent hover:bg-emerald-500',
  /**
   * 反向 — 終態，按下去這張單就結束（駁回 / 覆核的退回）。
   *
   * ⚠ 2026-08-27 使用者定調：「往後是紅色 但是是紅色框就好，不要有紅色實心」。
   * 所以 `stop` 與 `back` **現在長得一模一樣**。 兩個名字仍然分開，是因為呼叫端的
   * 語意不同（終態 vs 可逆），日後要把濃淡分回來只改這一行。
   * 「終不終」現在靠**標籤**（✕ 駁回 vs ↩ 退回補件）與**確認框**講清楚，
   * 不靠顏色 —— 駁回那顆按下去會先跳確認框說明「這張單就結束了、救不回來」。
   */
  stop: 'text-red-700 border-red-200 hover:bg-red-50',
  /** 反向 — 可逆，退回去改 / 撤回 / 不付了，單還在。 */
  back: 'text-red-700 border-red-200 hover:bg-red-50',
  /**
   * 「球丟回申請人」—— 只給**退回補件**用。 實心。
   *
   * 2026-09-06 使用者:「退回補件 應該在核准旁邊 然後也用一個實心的顏色」。
   * ⚠ 這跟 2026-08-27 定調的「不要有紅色實心」不衝突 —— 實心的是**琥珀**不是紅：
   *   退回補件不是否決, 它是「這張單還活著, 只是換你處理」, 跟 §6.1 的待處理同色。
   *   駁回 (stop) 維持紅框, 這樣兩顆一眼分得出「還有救 / 沒救了」。
   * 不併進 back —— back 還被「作廢 / 取消出差 / 退回上一關」用著, 那些不該變實心。
   */
  send_back: 'bg-amber-500 text-white border-transparent hover:bg-amber-400',
  /** 既非正向也非反向 — 「還不決定」（延後付款）。 §6.1 的 pending 桶。 */
  hold: 'text-amber-700 border-amber-200 hover:bg-amber-50',
  /**
   * 中性 — 同一列裡「什麼都不做」的出口（小視窗的「取消」＝關掉視窗）。
   * 它不是決策，但要跟旁邊的決策鈕**同寬同高**，不然那一列還是不齊。
   */
  quiet: 'text-slate-700 border-slate-300 hover:bg-slate-50',
} as const;

export type DecisionTone = keyof typeof TONE;

export function decisionCls(tone: DecisionTone, extra = ''): string {
  return `${BASE} ${TONE[tone]}${extra ? ` ${extra}` : ''}`;
}

/**
 * 表格列內的版本 —— 列高 40px 放得下的唯一尺寸（§5.0 尺寸表）。
 * min-width 比詳情頁小：一列最多兩顆, 而動作欄只有 160px。
 */
const ROW_BASE =
  'inline-flex items-center justify-center gap-1 min-w-[4.5rem] px-2 py-1 text-xs rounded border whitespace-nowrap disabled:opacity-50';

export function rowDecisionCls(tone: DecisionTone, extra = ''): string {
  return `${ROW_BASE} ${TONE[tone]}${extra ? ` ${extra}` : ''}`;
}
